'use client'
// design-system: tokens use --tls-*/--tlsr-* namespace (not --color-*); see docs/design-system/tokens.md

import { useState, useCallback, useRef, useEffect } from 'react'
import { Download, AlertCircle, Loader2, X } from 'lucide-react'
import Link from 'next/link'
import { VoiceSelector } from './voice-selector'
import { TPlay } from '@/components/ui/tls/t-play'
import { VoiceLengthMeter } from './voice-length-meter'
import { CreditsDisplay } from './credits-display'
import { OpenInChimeStudio } from './open-in-chime-studio'
import { useSessionLog } from './session-log'
import { ActivityRailDesktop, ActivityStatusRow } from './activity-rail'
import { optimizeForTeslaWithFades, TESLA_REQUIREMENTS } from '@/lib/audio-utils'
import { SilentClipError, trimSilence } from '@/lib/trim-silence'
import { assessLength, formatSeconds, MAX_CLIP_SECONDS, MAX_SPOKEN_CHARS, MAX_TYPED_CHARS, spokenText } from '@/lib/voice-length'
import { VOICE_SAMPLE_LINE } from '@/lib/voice-catalog'
import { getRequestProof, withRequestProofHeaders } from '@/lib/request-proof'
import { VOICE_MOODS } from '@/lib/voice-moods'
import { trackCreateStep, trackDownloadCompleted, trackDownloadButtonClick, trackDownloadInitiated, trackPreviewPlay } from '@/lib/tracking'
import { markDownloadCompleted } from '@/lib/download-attribution'
import { soundsApi } from '@/lib/api/sounds'
import { cn } from '@/lib/utils'
import { SectionHead } from '@/components/ui/tls/section-head'
import { announce } from '@/lib/announce'
import { getAudioCoordinator } from '@/lib/audio-coordinator'
import { TESLA_FACTS } from '@/lib/tesla-facts'
import Sentry from '@/lib/sentry-safe'

const isDev = process.env.NODE_ENV === 'development'

const GENERATION_TIMEOUT_MS = 90_000
const PREVIEW_COORDINATOR_ID = 'voice-preview'
const EXAMPLE_LINES = ['Goodbye!', 'See you later!', 'Locked and loaded', 'Stay safe out there', '[whispers] Locked.']
const UNKNOWN_CHARGE_NOTICE = 'This attempt may have used 1 voice credit. Check the balance before trying again.'
const CREDITS_EXHAUSTED = 'No voice credits left today. Credits reset at midnight UTC.'
const BALANCE_UNAVAILABLE = 'Voice credits could not be checked. Reload the page to try again.'
/** Carries the HTTP status so the catch block can tell a real failure from an
 *  expected refusal (e.g. the daily quota 429). */
class VoiceGenerationError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'VoiceGenerationError'
    this.status = status
  }
}

interface VoiceInfo {
  id: string
  name: string
  description: string
  sample?: string
  pace?: number
}

interface VoiceDraft {
  text: string
  voiceId: string
  voiceName: string
  mood: string | null
}

/** What the finished file turned out to be, measured after silence is trimmed. */
interface ClipInfo {
  seconds: number
  cut: boolean
}

interface VoiceJob {
  controller: AbortController
  cancelled: boolean
  draft: VoiceDraft
}

interface GenerationState {
  remaining: number
  total: number
  voices: VoiceInfo[]
}

/** §19.6's six states, minus QUEUED and BLOCKED — this product has no
 * server-side queue to report a position from, and "blocked" is simply
 * `jobState === 'running'` disabling the Generate button rather than a
 * state of its own. */
type JobState = 'idle' | 'running' | 'succeeded' | 'failed' | 'cancelled'

// The exact strings lib/request-proof.ts and the API's request-proof guard
// emit when they refuse a request. Matched verbatim so an unrelated error
// whose message merely mentions a request is never mistaken for the gate.
const REQUEST_PROOF_REFUSALS = new Set([
  'Forbidden request source',
  'Unable to obtain request challenge',
  'Unable to verify request identity',
])

function endSentence(message: string): string {
  return /[.!?]$/.test(message) ? message : `${message}.`
}

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000)
  const mins = Math.floor(s / 60)
  const secs = s % 60
  return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`
}

// eslint-disable-next-line react-doctor/no-giant-component -- voice generator is a self-contained feature with generation, preview, and download flows; splitting would require complex state lifting
// eslint-disable-next-line react-doctor/prefer-useReducer -- multiple independent state slices across generation/audio/processing phases; useReducer would not simplify this pattern
export function VoiceGenerator() {
  // Text input state
  const [text, setText] = useState('')
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>('')
  const [mood, setMood] = useState<string | null>(null)

  // §19.6 job state — replaces the old boolean isGenerating with the full
  // asynchronous-job state machine (running/succeeded/failed/cancelled).
  const [jobState, setJobState] = useState<JobState>('idle')
  const [elapsedMs, setElapsedMs] = useState(0)
  const [generationState, setGenerationState] = useState<GenerationState | null>(null)
  const [balanceAvailable, setBalanceAvailable] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isGenerating = jobState === 'running'

  const activeJobRef = useRef<VoiceJob | null>(null)
  const [generatedDraft, setGeneratedDraft] = useState<VoiceDraft | null>(null)
  const jobStartRef = useRef<number>(0)
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const announced30Ref = useRef(false)
  const announced60Ref = useRef(false)

  // §19.2/§19.10 — the editor's one session log; the voice route's activity
  // rail is the natural host for job status (§19.6).
  const sessionLog = useSessionLog()

  const clearJobTimers = useCallback(() => {
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current)
      elapsedIntervalRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => () => {
    clearJobTimers()
    const job = activeJobRef.current
    activeJobRef.current = null
    job?.controller.abort()
  }, [clearJobTimers])

  // Audio state
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [processedBlob, setProcessedBlob] = useState<Blob | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [hasDownloaded, setHasDownloaded] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const audioUrlRef = useRef<string | null>(null)

  const selectedVoice = generationState?.voices.find(voice => voice.id === selectedVoiceId)
  const length = assessLength(text)
  const isOverLimit = length.state === 'over'
  const [clipInfo, setClipInfo] = useState<ClipInfo | null>(null)
  const [quotaRefused, setQuotaRefused] = useState(false)

  // Fetch initial state (voices and remaining credits)
  // eslint-disable-next-line react-doctor/no-fetch-in-effect -- initial metadata fetch on mount; SWR refactor would require extracting to a hook which is out of scope for this component
  useEffect(() => {
    async function fetchState() {
      try {
        const response = await fetch('/api/generate-voice')
        if (response.ok) {
          const data = await response.json()
          // eslint-disable-next-line react-doctor/no-chain-state-updates -- two independent state slices set from one fetch response; combining into one state object would require a larger refactor
          setGenerationState(data)
          setBalanceAvailable(true)
          // Set default voice only on first load (selectedVoiceId is '' initially)
          setSelectedVoiceId((prev) => (!prev && data.voices?.length > 0 ? data.voices[0].id : prev))
        } else {
          setError(BALANCE_UNAVAILABLE)
        }
      } catch (err) {
        setError(BALANCE_UNAVAILABLE)
        if (isDev) console.error('Failed to fetch voice state:', err)
      }
    }
    fetchState()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs once on mount; selectedVoiceId dependency would cause double-fetch on every voice selection
  }, [])

  // Cleanup audio URL on unmount
  useEffect(() => {
    const urlRef = audioUrlRef;
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current)
      }
    }
  }, [])

  // Stop any in-flight preview playback. Must run whenever the blobs are
  // cleared: the preview button unmounts with them, and without a pause the
  // detached <audio> element keeps playing with no visible control.
  const stopPreview = useCallback(() => {
    audioRef.current?.pause()
    setIsPlaying(false)
  }, [])

  // A voice sample starting elsewhere on the page stops this preview.
  useEffect(() => getAudioCoordinator().subscribe(PREVIEW_COORDINATOR_ID, stopPreview), [stopPreview])

  const clearGeneratedAudio = useCallback(() => {
    stopPreview()
    setAudioBlob(null)
    setProcessedBlob(null)
    setGeneratedDraft(null)
    setClipInfo(null)
    setHasDownloaded(false)
    setError(null)
    if (!activeJobRef.current) setJobState('idle')
  }, [stopPreview])

  const processToWav = useCallback(async (mp3Blob: Blob): Promise<{ wav: Blob; clip: ClipInfo }> => {
    const arrayBuffer = await mp3Blob.arrayBuffer()
    const audioContext = new AudioContext()
    try {
      // audio-egress-allow: audio the visitor just generated on /voice
      const decoded = await audioContext.decodeAudioData(arrayBuffer)
      // Spend the clip limit on speech: drop the silence the model leaves at
      // both ends before the file is cut to length.
      const { buffer } = trimSilence(decoded)
      const wav = await optimizeForTeslaWithFades(buffer, {
        volumePercent: 100,
        fadeIn: 0.02,
        fadeOut: 0.05,
      })
      return {
        wav,
        clip: {
          seconds: Math.min(buffer.duration, MAX_CLIP_SECONDS),
          cut: buffer.duration > MAX_CLIP_SECONDS + 0.005,
        },
      }
    } catch (err) {
      if (err instanceof SilentClipError) throw err
      throw new Error('Failed to convert audio. Please try again.', { cause: err })
    } finally {
      await audioContext.close()
    }
  }, [])

  // Generate voice — §19.6 asynchronous job. The editor stays live
  // throughout: nothing else on the page is blocked while this runs.
  const handleGenerate = useCallback(async () => {
    if (activeJobRef.current) return
    if (!balanceAvailable || !generationState || generationState.remaining <= 0) return

    if (!text.trim() || !selectedVoiceId) {
      setError('Please enter text and select a voice')
      return
    }

    if (isOverLimit) {
      setError(`That runs past ${MAX_CLIP_SECONDS} seconds. Keep the spoken words to ${MAX_SPOKEN_CHARS} characters or fewer.`)
      return
    }

    const controller = new AbortController()
    const job: VoiceJob = {
      controller,
      cancelled: false,
      draft: {
        text: text.trim(),
        voiceId: selectedVoiceId,
        voiceName: generationState?.voices.find(voice => voice.id === selectedVoiceId)?.name || selectedVoiceId,
        mood,
      },
    }
    activeJobRef.current = job
    const requireActiveJob = () => {
      if (activeJobRef.current !== job || controller.signal.aborted) throw new DOMException('Aborted', 'AbortError')
    }
    announced30Ref.current = false
    announced60Ref.current = false
    jobStartRef.current = Date.now()

    setJobState('running')
    setElapsedMs(0)
    setError(null)
    setQuotaRefused(false)
    stopPreview()
    setAudioBlob(null)
    setProcessedBlob(null)
    setGeneratedDraft(null)
    setClipInfo(null)
    setHasDownloaded(false)

    clearJobTimers()
    elapsedIntervalRef.current = setInterval(() => {
      const ms = Date.now() - jobStartRef.current
      setElapsedMs(ms)
      // §19.6: reduced-motion honest substitute for the sweep animation —
      // the counter also announces at 30s and 60s so a screen reader user
      // is not left in silence for up to 90 seconds.
      if (ms >= 30_000 && !announced30Ref.current) {
        announced30Ref.current = true
        announce('Still generating, 30 seconds elapsed.', 'polite')
      } else if (ms >= 60_000 && !announced60Ref.current) {
        announced60Ref.current = true
        announce('Still generating, 60 seconds elapsed.', 'polite')
      }
    }, 1000)
    timeoutRef.current = setTimeout(() => controller.abort(), GENERATION_TIMEOUT_MS)

    const refreshBalance = async (response?: Response): Promise<number | null> => {
      try {
        const stateResponse = await fetch('/api/generate-voice')
        if (stateResponse.ok) {
          const stateData = await stateResponse.json()
          if (activeJobRef.current !== job) return null
          setGenerationState(prev => prev ? { ...prev, ...stateData } : stateData)
          setBalanceAvailable(true)
          return stateData.remaining ?? null
        }
      } catch {
      }
      if (activeJobRef.current !== job) return null
      const remaining = response?.headers.get('X-TTS-Remaining')
      const total = response?.headers.get('X-TTS-Total')
      if (remaining && total) {
        setGenerationState(prev => prev ? { ...prev, remaining: parseInt(remaining), total: parseInt(total) } : prev)
        setBalanceAvailable(true)
        return parseInt(remaining)
      }
      setBalanceAvailable(false)
      return null
    }

    try {
      // The route requires the HMAC request proof (TES-113) — same header
      // every other mutation endpoint gets via withRequestProofHeaders.
      const postGenerate = async () => {
        const headers = await withRequestProofHeaders({ 'Content-Type': 'application/json' })
        requireActiveJob()
        return fetch('/api/generate-voice', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            text: job.draft.text,
            voiceId: job.draft.voiceId,
            ...(job.draft.mood ? { mood: job.draft.mood } : {}),
          }),
          signal: controller.signal,
        })
      }

      let response = await postGenerate()
      requireActiveJob()
      if (response.status === 403) {
        // Proof expired or identity cookie rotated — mint a fresh proof and
        // retry once before surfacing the error.
        await getRequestProof(true)
        response = await postGenerate()
        requireActiveJob()
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new VoiceGenerationError(
          errorData.error || 'Failed to generate voice',
          response.status,
        )
      }

      // Get MP3 audio data
      const mp3Data = await response.arrayBuffer()
      const mp3Blob = new Blob([mp3Data], { type: 'audio/mpeg' })
      requireActiveJob()

      const updatedRemaining = await refreshBalance(response)

      requireActiveJob()
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      setIsProcessing(true)
      const { wav: wavBlob, clip } = await processToWav(mp3Blob)
      requireActiveJob()
      setAudioBlob(mp3Blob)
      setProcessedBlob(wavBlob)
      setClipInfo(clip)
      setGeneratedDraft(job.draft)
      clearJobTimers()
      setJobState('succeeded')
      trackCreateStep({ tool: 'voice', step: 'voice_generated' })
      sessionLog.addEntry(
        'credit',
        `Spent 1 voice credit${updatedRemaining !== null ? `, ${updatedRemaining} left today` : ''}`,
        { undoable: false }
      )
      sessionLog.addEntry('generation', 'Voice clip ready to preview', { undoable: false })
    } catch (err) {
      if (activeJobRef.current !== job) return
      clearJobTimers()
      const elapsedSeconds = Math.round((Date.now() - jobStartRef.current) / 1000)
      await refreshBalance()
      if (activeJobRef.current !== job) return

      if (controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
        if (job.cancelled) {
          setJobState('cancelled')
          sessionLog.addEntry('generation', `Cancelled. ${UNKNOWN_CHARGE_NOTICE}`, { undoable: false })
        } else {
          trackCreateStep({ tool: 'voice', step: 'failed', reason: 'timeout' })
          setJobState('failed')
          setError(`Generation failed after ${GENERATION_TIMEOUT_MS / 1000} seconds. The voice service did not respond. ${UNKNOWN_CHARGE_NOTICE}`)
          sessionLog.addEntry(
            'generation',
            `Generation failed after ${GENERATION_TIMEOUT_MS / 1000} seconds. ${UNKNOWN_CHARGE_NOTICE}`,
            { undoable: false, announce: 'assertive' }
          )
        }
        return
      }

      // The request-proof gate refusing us surfaces two ways: /api/challenge
      // 403s inside lib/request-proof.ts, which throws a plain Error carrying
      // the server's own words ("Forbidden request source", "Unable to obtain
      // request challenge", "Unable to verify request identity"), or the
      // generate route 403s after the one proof retry. None of those phrases
      // is a sentence a person can act on, so they are translated here: what
      // happened, what to do, what it cost. Other server messages (the 429
      // quota refusal, the unconfigured-service line) are already written
      // for the user and pass through.
      const isProofRefusal =
        (err instanceof VoiceGenerationError && err.status === 403) ||
        (err instanceof Error && REQUEST_PROOF_REFUSALS.has(err.message))
      const noCreditSpent = isProofRefusal || (err instanceof VoiceGenerationError && (err.status === 400 || err.status === 429))
      const message = isProofRefusal
        ? 'We could not confirm this request came from the site, so nothing was generated. Reload the page and try again. No credit was spent.'
        : err instanceof TypeError && /fetch|network/i.test(err.message)
          ? 'We could not reach the voice service. Check your connection and try again.'
          : err instanceof Error
            ? err.message
            : 'Failed to generate voice'
      trackCreateStep({
        tool: 'voice',
        step: 'failed',
        reason: err instanceof VoiceGenerationError ? `http_${err.status}` : 'error',
      })
      setJobState('failed')
      setError(noCreditSpent ? message : `${endSentence(message)} ${UNKNOWN_CHARGE_NOTICE}`)
      sessionLog.addEntry(
        'generation',
        `Generation failed after ${elapsedSeconds} seconds. ${noCreditSpent ? 'No credit was spent.' : UNKNOWN_CHARGE_NOTICE}`,
        { undoable: false, announce: 'assertive' }
      )
      // A 429 is the designed daily-quota refusal from lib/voice-generation-limits —
      // the user reads it inline and comes back tomorrow. Reporting it made a
      // product rule look like an application error in Sentry
      // (TESLA-LOCK-SOUND-54, TES-1872), so only unexpected failures go up.
      // 400 is the same rule one route earlier: every 400 the generate route
      // emits is an input refusal it already wrote for the reader — text too
      // long, text empty, unknown voice, and the profanity screen in
      // lib/content-moderation. "Contains inappropriate language" reaching
      // Sentry as an error (TESLA-LOCK-SOUND-69) is the moderation gate doing
      // its job, not a defect. 403 stays reported: that one means the request
      // proof did not verify, which can be ours.
      const isExpectedRefusal =
        err instanceof SilentClipError || (err instanceof VoiceGenerationError && (err.status === 429 || err.status === 400))
      setQuotaRefused(err instanceof VoiceGenerationError && err.status === 429)
      if (!isExpectedRefusal) {
        Sentry.captureException(err, { tags: { context: 'VoiceGenerator', action: 'generate' } })
      }
    } finally {
      if (activeJobRef.current === job) {
        activeJobRef.current = null
        clearJobTimers()
        setIsProcessing(false)
      }
    }
  }, [balanceAvailable, generationState, text, selectedVoiceId, mood, isOverLimit, processToWav, sessionLog, clearJobTimers, stopPreview])

  // §19.6: Cancel is always focusable and the panel's last stop; Esc does
  // NOT cancel (an accidental Esc costing a credit is unacceptable) — this
  // is only reachable via the explicit button.
  const handleCancel = useCallback(() => {
    const job = activeJobRef.current
    if (!job) return
    job.cancelled = true
    job.controller.abort()
  }, [])

  // Play/pause preview
  const handlePlayPause = useCallback(() => {
    if (!audioBlob || !generatedDraft) return

    if (isPlaying && audioRef.current) {
      audioRef.current.pause()
      setIsPlaying(false)
      getAudioCoordinator().releasePlayback(PREVIEW_COORDINATOR_ID)
    } else {
      getAudioCoordinator().requestPlayback(PREVIEW_COORDINATOR_ID)
      // Create new audio element
      trackPreviewPlay({
        sound_id: 'ai-voice-generated',
        sound_title: `AI Voice: ${generatedDraft.text.slice(0, 50)}${generatedDraft.text.length > 50 ? '...' : ''}`,
        source: 'voice_generator',
      })

      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current)
      }
      audioUrlRef.current = URL.createObjectURL(audioBlob)

      if (!audioRef.current) {
        audioRef.current = new Audio()
      }
      // audio-egress-allow: audio the visitor just generated on /voice
      audioRef.current.src = audioUrlRef.current
      audioRef.current.onended = () => {
        setIsPlaying(false)
        getAudioCoordinator().releasePlayback(PREVIEW_COORDINATOR_ID)
      }
      audioRef.current.play().catch(() => setIsPlaying(false))
      setIsPlaying(true)
    }
  }, [audioBlob, isPlaying, generatedDraft])

  // Download as LockChime.wav
  const handleDownload = useCallback(() => {
    if (!processedBlob || !generatedDraft) return
    const soundTitle = `AI Voice: ${generatedDraft.text.slice(0, 50)}${generatedDraft.text.length > 50 ? '...' : ''}`

    trackDownloadButtonClick({
      sound_id: 'ai-voice-generated',
      sound_title: soundTitle,
      source: 'voice_generator',
    })
    trackDownloadInitiated({
      sound_id: 'ai-voice-generated',
      sound_title: soundTitle,
      source: 'voice_generator',
    })

    const url = URL.createObjectURL(processedBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = TESLA_REQUIREMENTS.FILENAME // LockChime.wav
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    // Track download completion for GA4
    trackDownloadCompleted({
      sound_id: 'ai-voice-generated',
      sound_title: soundTitle,
      source: 'voice_generator',
      trim_used: false,
    })
    // Arm /guide attribution so a follow-on guide visit fires
    // `guide_visited_post_download` (TES-1577 — this path recorded none).
    markDownloadCompleted({
      sound_id: 'ai-voice-generated',
      sound_title: soundTitle,
      source: 'voice_generator',
    })
    void soundsApi.accountDownload({})

    setHasDownloaded(true)
  }, [processedBlob, generatedDraft])

  const balanceLoading = generationState === null && !balanceAvailable && !error
  const generateBlockedReason = isGenerating
    ? 'A voice is already generating.'
    : balanceLoading
      ? 'Checking your voice credits.'
      : !balanceAvailable
        ? BALANCE_UNAVAILABLE
        : generationState && generationState.remaining <= 0
          ? CREDITS_EXHAUSTED
          : spokenText(text).length < 2
            ? 'Type at least 2 characters of words to generate.'
            : isOverLimit
              ? `Shorten the line to ${MAX_SPOKEN_CHARS} spoken characters or fewer to generate.`
              : !selectedVoiceId
                ? 'Pick a voice to generate.'
                : null
  const canGenerate = balanceAvailable && generationState !== null && generationState.remaining > 0 && spokenText(text).length >= 2 && selectedVoiceId && !isOverLimit && !isGenerating
  const hasAudio = audioBlob !== null
  const hasProcessedAudio = processedBlob !== null

  const showAlternative = (generationState !== null && generationState.remaining <= 0) || quotaRefused
  // One accent primary per panel: Generate is the primary until a clip exists, then Download is.
  const generateIsPrimary = !hasProcessedAudio

  return (
    <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-6">
    <div className="min-w-0 flex-1 space-y-8">
      {/* Panel 1, source: the line */}
      <section aria-labelledby="voice-line-heading">
        <SectionHead size="block" id="voice-line-heading">Your line</SectionHead>
        <div className="space-y-4">
          <textarea
            value={text}
            onChange={event => { clearGeneratedAudio(); setText(event.target.value) }}
            aria-label={`Your message, up to ${MAX_SPOKEN_CHARS} characters`}
            aria-describedby="voice-length voice-tag-hint"
            placeholder="What should your Tesla say? For example: Goodbye, drive safe!"
            className={cn(
              'w-full min-h-24 max-h-[12lh] field-sizing-content px-4 py-3 bg-[var(--tls-surface)] rounded-[var(--ls-radius-0)] text-[var(--tls-fg-strong)] placeholder:text-[var(--tls-fg-subtle)] resize-none focus:outline-none focus:ring-2 focus:ring-[var(--tls-brand)] transition-colors duration-[var(--motion-duration-standard)]',
              // §02.2: error is a doubled inset ink border plus copy, never colour alone —
              // the system has no red.
              isOverLimit
                ? 'border-2 border-[var(--tls-fg-strong)] shadow-[inset_0_0_0_1px_var(--tls-fg-strong)]'
                : 'border border-[var(--tls-border-subtle)]'
            )}
            maxLength={MAX_TYPED_CHARS}
          />

          <VoiceLengthMeter id="voice-length" text={text} pace={selectedVoice?.pace} />

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-[var(--tls-fg-subtle)]">Try:</span>
            {EXAMPLE_LINES.map((suggestion) => (
              <button type="button"
                key={suggestion}
                onClick={() => { clearGeneratedAudio(); setText(suggestion) }}
                // min-h + inline-flex/items-center ensures 44px WCAG 2.5.5 touch target
                className="inline-flex items-center min-h-[44px] text-sm px-2 py-1 bg-[var(--tls-surface)] hover:bg-[var(--tls-surface-hover)] border border-[var(--tls-border-subtle)] rounded-[var(--ls-radius-0)] text-[var(--tls-fg-muted)] hover:text-[var(--tls-fg-strong)] transition-colors duration-[var(--motion-duration-standard)]"
              >
                {suggestion}
              </button>
            ))}
          </div>

          <div className="space-y-3 border-t border-[color:var(--tls-border-subtle)] pt-4">
            <p id="voice-tag-hint" className="text-sm text-[var(--tls-fg-muted)]">
              Direct the delivery with a tag in square brackets before the words it changes, such as{' '}
              <code className="break-words text-[var(--tls-fg-strong)]">[whispers] Locked.</code> The v4 voices follow{' '}
              <code className="break-words">[whispers]</code>, <code className="break-words">[excited]</code>,{' '}
              <code className="break-words">[sighs]</code>, <code className="break-words">[laughs]</code> and{' '}
              <code className="break-words">[curious]</code>. Results vary by voice.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-[var(--tls-fg-subtle)]">Or add one for me:</span>
              {VOICE_MOODS.map((m) => {
                const active = mood === m
                return (
                  <button type="button"
                    key={m}
                    onClick={() => { clearGeneratedAudio(); setMood(active ? null : m) }}
                    aria-pressed={active}
                    className={cn(
                      'inline-flex items-center min-h-[44px] text-sm px-3 py-1 border rounded-[var(--ls-radius-0)] transition-colors duration-[var(--motion-duration-standard)]',
                      active
                        ? 'border-[color:var(--tls-fg-strong)] bg-[var(--tls-tint-selected)] font-semibold text-[var(--tls-fg-strong)]'
                        : 'bg-[var(--tls-surface)] hover:bg-[var(--tls-surface-hover)] border-[var(--tls-border-subtle)] text-[var(--tls-fg-muted)] hover:text-[var(--tls-fg-strong)]'
                    )}
                  >
                    {m}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Panel 2, surface: the voice */}
      <section aria-labelledby="voice-choose-heading" className="pt-4 border-t border-[var(--tls-border-subtle)]">
        <SectionHead
          size="block"
          id="voice-choose-heading"
          lede={`Press play on a row to hear “${VOICE_SAMPLE_LINE}” in that voice. Samples use no credit.`}
        >
          Voice
        </SectionHead>
        <VoiceSelector
          voices={generationState?.voices || []}
          selectedVoiceId={selectedVoiceId}
          onSelect={voiceId => { clearGeneratedAudio(); setSelectedVoiceId(voiceId) }}
        />
      </section>

      {/* Panel 3, controls: spend a credit */}
      <section aria-labelledby="voice-generate-heading" className="pt-4 border-t border-[var(--tls-border-subtle)]">
        <SectionHead size="block" id="voice-generate-heading">Generate</SectionHead>
        <div className="space-y-4">
          {generationState && balanceAvailable && (
            <CreditsDisplay
              remaining={generationState.remaining}
              total={generationState.total}
            />
          )}
          {/* Error display — ink weight carries it, not colour; no semantic red (§01.2) */}
          {error && jobState !== 'running' && (
            <div className="flex items-start gap-3 pt-3" role="alert">
              <AlertCircle className="size-5 text-[var(--tls-fg-strong)] flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-[var(--tls-fg-strong)]">{error}</p>
            </div>
          )}

          {/* §19.6 job panel — the editor stays live throughout; this panel is
              the only thing that changes while a generation runs. role=status
              aria-live=polite: "start, completion, failure and cancellation
              each announce once." */}
          <div role="status" aria-live="polite" aria-busy={jobState === 'running'}>
            {jobState === 'running' ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-[var(--tls-fg-strong)]">
                    {isProcessing ? 'Preparing LockChime.wav' : 'Generating voice'}
                  </p>
                  {/* Elapsed counter — aria-hidden: the visual/reduced-motion
                      substitute for the sweep bar. The 30s/60s announce()
                      calls in handleGenerate cover the screen-reader case. */}
                  <span aria-hidden="true" className="shrink-0 font-sans text-sm font-semibold tabular-nums text-[var(--tls-fg-subtle)]">
                    {formatElapsed(elapsedMs)} elapsed
                  </span>
                </div>
                <VoiceGenerationProgressBar />
                <p className="text-sm text-[var(--tls-fg-muted)]">
                  You can keep editing your text while this runs.
                </p>
                {/* §19.6: Cancel is always focusable and the panel's last stop.
                    Esc does not cancel. */}
                <button type="button"
                  onClick={handleCancel}
                  className="inline-flex min-h-[44px] w-fit items-center gap-2 border border-[color:var(--tls-border-subtle)] px-4 text-sm font-medium text-[var(--tls-fg-strong)] transition-colors duration-[var(--motion-duration-standard)] hover:border-[color:var(--tls-border-strong)]"
                >
                  <X className="size-4" aria-hidden="true" />
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {jobState === 'cancelled' && (
                  <p className="text-sm text-[var(--tls-fg-muted)]">Cancelled. {UNKNOWN_CHARGE_NOTICE}</p>
                )}
                {/* Generate button — §19.7: cost is in the label, and the
                    resulting balance is beside it, so a cost is never
                    discovered by having been charged. */}
                <button type="button"
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                  aria-describedby="voice-generate-note"
                  className={cn(
                    'w-full py-4 font-semibold rounded-[var(--ls-radius-0)] transition-colors duration-[var(--motion-duration-standard)] flex items-center justify-center gap-2 disabled:bg-[var(--tls-surface-hover)] disabled:text-[var(--tls-fg-muted)] disabled:border-transparent disabled:cursor-not-allowed',
                    generateIsPrimary
                      ? 'bg-[var(--tls-brand)] hover:bg-[var(--tls-brand-hover)] text-[var(--tls-brand-fg)]'
                      : 'border border-[color:var(--tls-border-strong)] bg-transparent text-[var(--tls-fg-strong)] hover:bg-[var(--tls-surface-hover)]'
                  )}
                >
                  <span>
                    {jobState === 'succeeded' ? 'Generate again · 1 credit' : jobState === 'failed' ? 'Try again · 1 credit' : 'Generate voice · 1 credit'}
                  </span>
                  {generationState && balanceAvailable && generationState.remaining > 0 && (
                    <span className="font-medium tabular-nums">
                      · {Math.max(0, generationState.remaining - 1)} will remain
                    </span>
                  )}
                </button>
                <p id="voice-generate-note" className="text-sm text-[var(--tls-fg-muted)]">
                  {generateBlockedReason ? `${generateBlockedReason} ` : ''}Generating sends your text to ElevenLabs, a third-party voice service, to be spoken. Failed or cancelled generations can still use 1 credit.
                </p>
              </div>
            )}
          </div>

          {showAlternative && (
            <p className="border-t border-[color:var(--tls-border-subtle)] pt-4 text-sm text-[var(--tls-fg-muted)]">
              Need a lock sound today? The{' '}
              <Link href="/convert" className="text-[var(--tls-brand-ink)] underline underline-offset-4">converter</Link>{' '}
              turns any audio file into a LockChime.wav with no credits, and the{' '}
              <Link href="/sounds" className="text-[var(--tls-brand-ink)] underline underline-offset-4">library</Link>{' '}
              has ready-made sounds that install the same way.
            </p>
          )}
        </div>
      </section>

      {/* Panel 4, verdict and export: the clip */}
      <section aria-labelledby="voice-clip-heading" className="pt-4 border-t border-[var(--tls-border-subtle)]">
        <SectionHead size="block" id="voice-clip-heading">Your clip</SectionHead>
        {hasAudio ? (
          <div className="space-y-4">
            {generatedDraft && (
              <div className="text-sm text-[var(--tls-fg-muted)]">
                <p>Generated line: {generatedDraft.text}</p>
                <p>Voice: {generatedDraft.voiceName}</p>
                {generatedDraft.mood && <p>Mood: {generatedDraft.mood}</p>}
                {clipInfo && (
                  <p className={cn('tabular-nums', clipInfo.cut && 'font-semibold text-[var(--tls-fg-strong)]')}>
                    {clipInfo.cut
                      ? `The spoken line ran past ${MAX_CLIP_SECONDS} seconds, so the file stops at ${MAX_CLIP_SECONDS} s and the last words are cut. Shorten the line and generate again.`
                      : `Clip length ${formatSeconds(clipInfo.seconds)}. Silence at both ends is trimmed.`}
                  </p>
                )}
              </div>
            )}
            <div className="flex items-center gap-4">
              {/* A flipping "Pause preview" name alongside aria-pressed
                  double-signals the same transition and reads as the
                  contradictory "Pause preview, pressed". Stable name +
                  aria-pressed only (TES-1581). */}
              <TPlay size={52} title="preview" playing={isPlaying} onClick={handlePlayPause} />
              <div className="flex-1">
                <p className="text-sm text-[var(--tls-fg-strong)] font-medium">Preview</p>
                <p className="text-sm text-[var(--tls-fg-muted)]">Press play to hear it before you download.</p>
              </div>
            </div>

            <button type="button"
              onClick={handleDownload}
              disabled={!hasProcessedAudio || isProcessing}
              className="w-full py-4 bg-[var(--tls-brand)] hover:bg-[var(--tls-brand-hover)] disabled:bg-[var(--tls-surface-hover)] disabled:text-[var(--tls-fg-muted)] disabled:cursor-not-allowed text-[var(--tls-brand-fg)] font-semibold rounded-[var(--ls-radius-0)] transition-colors duration-[var(--motion-duration-standard)] flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="size-5 animate-spin" />
                  <span>Converting to Tesla format…</span>
                </>
              ) : (
                <>
                  <Download className="size-5" />
                  <span>Download LockChime.wav</span>
                </>
              )}
            </button>
            <p className="text-sm text-[var(--tls-fg-muted)]">
              WAV, {TESLA_FACTS.audio.recommended.sampleRate}, {TESLA_FACTS.audio.recommended.bitDepth}, up to {TESLA_FACTS.audio.recommended.maxDurationSeconds} seconds, under 1 MB. Tesla only needs the name:{' '}
              <code className="break-words text-[var(--tls-brand-ink)]">{TESLA_FACTS.audio.requiredFilename}</code>.
            </p>

            {/* Chime Studio link — for adjusting loudness and fades after generation */}
            <div className="flex justify-start pt-1">
              <OpenInChimeStudio variant="subtle" />
            </div>

            {/* Post-download: install guide bridge (generated voice has no soundId,
                so we mirror download-panel.tsx's no-soundId fallback rather than
                rendering the full PostDownloadPanel). */}
            {hasDownloaded && (
              <div className="pt-2 border-t border-[var(--tls-border-subtle)]">
                <p className="text-sm text-[var(--tls-fg-strong)] font-medium pt-4 mb-2">
                  Downloaded. Next, put it on a USB drive.
                </p>
                <Link
                  href="/guide"
                  className="inline-flex items-center gap-1.5 text-sm text-[var(--tls-brand-ink)] underline-offset-4 transition-colors duration-[var(--motion-duration-standard)] hover:text-[var(--tls-brand-ink-hover)] hover:underline"
                >
                  <span>Open the install guide</span>
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="border-y border-[color:var(--tls-border-subtle)] py-6 text-sm text-[var(--tls-fg-muted)]">
            <p className="font-medium text-[var(--tls-fg-strong)]">Nothing generated yet.</p>
            <p className="mt-1">
              Your clip shows up here to play before you download it. It saves as{' '}
              <code className="break-words text-[var(--tls-brand-ink)]">{TESLA_FACTS.audio.requiredFilename}</code>: a{' '}
              {TESLA_FACTS.audio.recommended.sampleRate}, {TESLA_FACTS.audio.recommended.bitDepth} WAV of up to {TESLA_FACTS.audio.recommended.maxDurationSeconds} seconds.
            </p>
          </div>
        )}
      </section>

      <ActivityStatusRow log={sessionLog.log} />
    </div>

    <ActivityRailDesktop log={sessionLog.log} className="lg:sticky lg:top-6" />
    </div>
  )
}

/**
 * §19.6 motion-sweep / reduced-motion pair for the indeterminate progress
 * bar. Defined as a scoped `<style>` rather than a `globals.css`/
 * `tailwind.config.ts` addition — both files belong to a sibling agent's
 * in-flight work on this branch.
 */
function VoiceGenerationProgressBar() {
  return (
    <div aria-hidden="true" className="relative h-1 overflow-hidden bg-[var(--tls-border-subtle)]">
      <style>{`
        @keyframes tls-voice-gen-sweep { from { left: -38%; } to { left: 100%; } }
        .tls-voice-gen-sweep-bar { position: absolute; top: 0; bottom: 0; width: 38%; background: var(--tls-brand); left: 0; }
        @media (prefers-reduced-motion: no-preference) {
          .tls-voice-gen-sweep-bar { animation: tls-voice-gen-sweep 1.1s linear infinite; }
        }
      `}</style>
      <div className="tls-voice-gen-sweep-bar" />
    </div>
  )
}
